import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnChanges, Output, signal, SimpleChanges } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ACTIVATION_EVENTS, Layout, LayoutGroup, LayoutRequest } from '../models/layout.model';
import { Modal } from '../../../shared/components/modal/modal';
import { ModalFooterDirective } from '../../../shared/components/modal/modal-footer.directive';
import { FormField } from '../../../shared/components/forms/form-field';
import { TextInput } from '../../../shared/components/forms/text-input';
import { NumberInput } from '../../../shared/components/forms/number-input';
import { Select } from '../../../shared/components/forms/select';

export type LayoutDialogMode = 'add' | 'edit';

@Component({
  selector: 'app-layout-dialog',
  standalone: true,
  imports: [ReactiveFormsModule, Modal, ModalFooterDirective, FormField, TextInput, NumberInput, Select],
  templateUrl: './layout-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LayoutDialogComponent implements OnChanges {
  private readonly fb = inject(FormBuilder);

  @Input() mode: LayoutDialogMode = 'add';
  @Input() layout: Layout | null = null;
  @Input() group!: LayoutGroup;
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<LayoutRequest>();

  readonly open = signal(true);
  // Spread the readonly tuple into a mutable array for app-select's options input.
  readonly activationEvents: string[] = [...ACTIVATION_EVENTS];

  form = this.fb.group({
    templateId: ['', Validators.required],
    groupOrderNum: [null as number | null],
    activationEvent: [null as string | null],
    activationButtons: [''],
    activationOfflineDelay: [null as number | null],
    slideshowDuration: [null as number | null],
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['layout']) {
      if (this.layout) {
        this.form.patchValue({
          templateId: this.layout.layoutId,
          groupOrderNum: this.layout.groupOrderNum ?? null,
          activationEvent: this.layout.activationEvent ?? null,
          activationButtons: this.layout.activationButtons ?? '',
          activationOfflineDelay: this.layout.activationOfflineDelay ?? null,
          slideshowDuration: this.layout.slideshowDuration ?? null,
        });
      } else {
        this.form.reset();
      }
    }
  }

  onClose(): void {
    this.close.emit();
  }

  onSubmit(): void {
    if (this.form.invalid) return;
    const v = this.form.value;

    const buttonsRaw = v.activationButtons?.trim() ?? '';
    const activationButtons = buttonsRaw
      ? buttonsRaw.split(',').map(b => b.trim()).filter(Boolean)
      : null;

    this.save.emit({
      templateId: v.templateId!,
      groupId: this.group.groupId,
      groupOrderNum: v.groupOrderNum ?? null,
      activationEvent: v.activationEvent ?? null,
      activationButtons,
      activationOfflineDelay: v.activationOfflineDelay ?? null,
      slideshowDuration: v.slideshowDuration ?? null,
      components: [],
    });
  }
}
